/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Temporary SSH/RDP access to a deployment's VMs (Swift API, coriolis.access):
 *
 *   GET    /{project}/deployments/{id}/access              grants, VMs with console links, who is allowed
 *   POST   /{project}/deployments/{id}/access              {"access": {"minutes": 1..max, "reason"}}
 *   DELETE /{project}/deployments/{id}/access/{grant_id}   closes it now
 */

import Api from "@src/utils/ApiCaller";
import configLoader from "@src/utils/Config";

export type AccessState = "pending" | "open" | "closed" | "failed" | "close_failed";

export type AccessGrant = {
  id: string;
  deployment_id: string;
  instance_id: string;
  instance_name: string | null;
  os_type: string | null;
  cidrs: string[];
  cidr_sources: { cidr: string; source: string }[];
  ports: number[];
  minutes: number;
  state: AccessState;
  trigger: "operator" | "deployment" | string;
  reason: string | null;
  requested_by: string | null;
  closed_by: string | null;
  opened_at: string | null;
  expires_at: string | null;
  closed_at: string | null;
  created_at: string | null;
  seconds_left: number | null;
  error: string | null;
  result: {
    applied?: string[];
    already?: string[];
    notes?: string[];
    public_ip?: string | null;
    private_ip?: string | null;
    removed?: string[];
  } | null;
};

export type ConsoleLink = {
  url?: string;
  label?: string;
  notes?: string[];
  extra?: { label: string; url: string }[];
  error?: string;
};

export type DeploymentAccess = {
  enabled: boolean;
  default_minutes: number;
  max_minutes: number;
  after_deployment: boolean;
  allowed: { cidr: string; source: string }[];
  vms: { instance_id: string; name: string; os_type: string | null; ports: number[]; console: ConsoleLink | null }[];
  grants: AccessGrant[];
};

const base = (deploymentId: string) =>
  `${configLoader.config.servicesUrls.coriolis}/${Api.projectId}/deployments/${encodeURIComponent(deploymentId)}/access`;

class AccessSource {
  async get(deploymentId: string): Promise<DeploymentAccess> {
    const response = await Api.send({ url: base(deploymentId), quietError: true, skipLog: true });
    return response.data.access;
  }

  async open(deploymentId: string, minutes: number, reason?: string): Promise<AccessGrant[]> {
    const response = await Api.send({
      url: base(deploymentId),
      method: "POST",
      data: { access: { minutes, reason: reason || undefined } },
      quietError: true,
      timeout: 120000,
    });
    return response.data.grants;
  }

  async close(deploymentId: string, grantId: string): Promise<AccessGrant> {
    const response = await Api.send({
      url: `${base(deploymentId)}/${encodeURIComponent(grantId)}`,
      method: "DELETE",
      quietError: true,
      timeout: 120000,
    });
    return response.data.grant;
  }
}

export default new AccessSource();
